import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TextField, MenuItem } from '@material-ui/core';
import { listCameras } from '../../Utils/cameraSettings';
import {
  POINT_WATCHING_CAMERA,
  POINT_WATCHING_CAMERA_HINT,
  GENERAL_CAMERA,
  GENERAL_CAMERA_HINT,
  DEFAULT_CAMERA_OPTION,
  CAMERA_NOT_ON_THIS_COMPUTER,
  SAME_CAMERA_WARNING,
} from './constants';

const DEFAULT_VALUE = '__default__';
const MISSING_VALUE = '__missing__';

// Two identical webcam models report the same label, so number repeats
// ("USB Camera", "USB Camera (2)") to keep them tellable apart.
const withDisplayNames = (cams) => {
  const seen = {};
  return cams.map((c, i) => {
    const base = c.label || `Camera ${i + 1}`;
    seen[base] = (seen[base] || 0) + 1;
    return {
      deviceId: c.deviceId,
      label: c.label,
      name: seen[base] > 1 ? `${base} (${seen[base]})` : base,
    };
  });
};

const stopVideo = (video) => {
  const stream = video && video.srcObject;
  if (stream) stream.getTracks().forEach((t) => t.stop());
  if (video) video.srcObject = null;
};

// Live preview of one camera, so the admin can see which physical camera
// a name belongs to before assigning it.
const CameraPreview = ({ deviceId }) => {
  const videoRef = useRef(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const video = videoRef.current;
    setError('');
    navigator.mediaDevices
      .getUserMedia({ audio: false, video: { deviceId: { exact: deviceId } } })
      .then((stream) => {
        if (cancelled || !videoRef.current) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => {});
      })
      .catch(() => {
        if (!cancelled) setError('Could not open this camera.');
      });
    return () => {
      cancelled = true;
      stopVideo(video);
    };
  }, [deviceId]);

  return (
    <div className="rule-camera-preview">
      {error ? (
        <span className="rule-camera-preview__error">{error}</span>
      ) : (
        <video ref={videoRef} muted playsInline />
      )}
    </div>
  );
};

const CameraSettingsSection = ({ value, onChange }) => {
  const [cameras, setCameras] = useState([]);
  const [detecting, setDetecting] = useState(false);
  const [detectError, setDetectError] = useState('');
  const [previewRole, setPreviewRole] = useState('');

  const refresh = useCallback(async () => {
    setCameras(withDisplayNames(await listCameras()));
  }, []);

  useEffect(() => {
    refresh();
    const md = navigator.mediaDevices;
    if (md && md.addEventListener) {
      md.addEventListener('devicechange', refresh);
      return () => md.removeEventListener('devicechange', refresh);
    }
    return undefined;
  }, [refresh]);

  // Chrome hides camera names until the site has camera permission. Opening
  // (and immediately closing) a stream is what grants it.
  const namesHidden =
    cameras.length > 0 && cameras.every((c) => !c.label || !c.deviceId);

  const detect = async () => {
    setDetecting(true);
    setDetectError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: true,
      });
      stream.getTracks().forEach((t) => t.stop());
      await refresh();
    } catch (err) {
      setDetectError(
        'Camera access was blocked. Allow camera access for this site in the browser, then try again.'
      );
    } finally {
      setDetecting(false);
    }
  };

  const selectValue = (role) => {
    const id = value[`${role}CameraId`];
    const label = value[`${role}CameraLabel`];
    if (!id && !label) return DEFAULT_VALUE;
    const match =
      cameras.find((c) => id && c.deviceId === id) ||
      cameras.find((c) => label && c.label === label);
    return match ? match.deviceId : MISSING_VALUE;
  };

  const handleSelect = (role) => (e) => {
    const picked = e.target.value;
    if (picked === MISSING_VALUE) return;
    if (picked === DEFAULT_VALUE) {
      onChange({ [`${role}CameraId`]: '', [`${role}CameraLabel`]: '' });
      if (previewRole === role) setPreviewRole('');
      return;
    }
    const cam = cameras.find((c) => c.deviceId === picked);
    onChange({
      [`${role}CameraId`]: picked,
      [`${role}CameraLabel`]: cam ? cam.label : '',
    });
  };

  const pwValue = selectValue('pointWatching');
  const generalValue = selectValue('general');
  const sameCamera =
    pwValue === generalValue &&
    pwValue !== DEFAULT_VALUE &&
    pwValue !== MISSING_VALUE;

  const renderRole = (role, title, hint, current) => {
    const savedLabel = value[`${role}CameraLabel`];
    const canPreview = current !== DEFAULT_VALUE && current !== MISSING_VALUE;
    return (
      <div
        className={`rule-camera-role ${
          role === 'general'
            ? 'rule-camera-role--spaced'
            : 'rule-camera-role--first'
        }`}
      >
        <TextField
          select
          className="input-manage-text-field"
          label={title}
          value={current}
          onChange={handleSelect(role)}
          InputLabelProps={{ shrink: true }}
          variant="outlined"
          helperText={
            current === MISSING_VALUE ? CAMERA_NOT_ON_THIS_COMPUTER : hint
          }
          error={current === MISSING_VALUE}
        >
          <MenuItem value={DEFAULT_VALUE}>{DEFAULT_CAMERA_OPTION}</MenuItem>
          {current === MISSING_VALUE && (
            <MenuItem value={MISSING_VALUE} disabled>
              {savedLabel || 'Saved camera'}
            </MenuItem>
          )}
          {cameras
            .filter((c) => c.deviceId)
            .map((c) => (
              <MenuItem key={c.deviceId} value={c.deviceId}>
                {c.name}
              </MenuItem>
            ))}
        </TextField>
        {canPreview && (
          <button
            type="button"
            className={
              'rule-preset-pill' + (previewRole === role ? ' active' : '')
            }
            onClick={() => setPreviewRole(previewRole === role ? '' : role)}
          >
            {previewRole === role ? 'Hide preview' : 'Preview'}
          </button>
        )}
        {canPreview && previewRole === role && (
          <CameraPreview deviceId={current} />
        )}
      </div>
    );
  };

  return (
    <div className="rule-match-section rule-match-section--on">
      <div className="form-fields rule-match-fields rule-ticket-photo-fields">
        {(namesHidden || cameras.length === 0) && (
          <div className="rule-camera-detect">
            <span className="rule-toggle-hint">
              {cameras.length === 0
                ? 'No cameras found on this computer yet.'
                : 'Camera names are hidden until this site is allowed to use the camera.'}
            </span>
            <button
              type="button"
              className="rule-preset-pill"
              onClick={detect}
              disabled={detecting}
            >
              {detecting ? 'Detecting…' : 'Detect cameras'}
            </button>
            {detectError && (
              <span className="rule-camera-preview__error">{detectError}</span>
            )}
          </div>
        )}
        {renderRole(
          'pointWatching',
          POINT_WATCHING_CAMERA,
          POINT_WATCHING_CAMERA_HINT,
          pwValue
        )}
        {renderRole(
          'general',
          GENERAL_CAMERA,
          GENERAL_CAMERA_HINT,
          generalValue
        )}
        {sameCamera && (
          <span className="rule-toggle-hint">{SAME_CAMERA_WARNING}</span>
        )}
      </div>
    </div>
  );
};

export default CameraSettingsSection;
